import Transform from '../math/Transform.js';
import GroundEffect from '../effects/GroundEffect.js';
import Debug from '../utils/Debug.js';

/**
 * Handles click-to-move functionality in game mode.
 * Converts clicks on world faces to movement commands.
 */
export default class ClickToMoveHandler {
  // Face direction offsets for side face clicks
  static FACE_OFFSETS = {
    front: { x: 0, z: 1 },
    back: { x: 0, z: -1 },
    left: { x: -1, z: 0 },
    right: { x: 1, z: 0 }
  };

  // Character dimensions for accessibility checks
  static CHARACTER_HEIGHT = 2.0;
  static CHARACTER_WIDTH = 0.9;

  /**
   * Create a click-to-move handler
   * @param {World} world - The game world
   * @param {Camera} camera - The camera
   * @param {PathFinder} pathFinder - The pathfinder
   * @param {NavGraph} navGraph - The navigation graph
   * @param {CharacterCollisionSystem} collisionSystem - For accessibility checks
   */
  constructor(world, camera, pathFinder, navGraph, collisionSystem = null) {
    this.world = world;
    this.camera = camera;
    this.pathFinder = pathFinder;
    this.navGraph = navGraph;
    this.collisionSystem = collisionSystem;
    this.player = null;
    this.enabled = false;

    // Destination marker (GroundEffect for proper depth sorting)
    this.destinationMarker = null;
    this.markerDestination = null;

    // Track mouse state for click detection
    this.mouseDownPos = null;
    this.CLICK_THRESHOLD = 5;

    this._createMarker();
    this._setupEventListeners();
  }

  /**
   * Set the player character
   * @param {Character} player - The player character
   */
  setPlayer(player) {
    this.player = player;

    // Set up callbacks for path follower
    if (player && player.pathFollower) {
      player.pathFollower.onComplete = () => this._hideMarker();
      player.pathFollower.onCancelled = () => this._hideMarker();
    }
  }

  /**
   * Enable click-to-move mode
   */
  enable() {
    this.enabled = true;
  }

  /**
   * Disable click-to-move mode
   */
  disable() {
    this.enabled = false;
    this.mouseDownPos = null;
    this._hideMarker();
  }

  /**
   * Create the destination marker as a GroundEffect for proper depth sorting
   * @private
   */
  _createMarker() {
    // Create a GroundEffect with destination marker styling
    this.destinationMarker = new GroundEffect({
      x: 0,
      y: 0,
      z: 0,
      radius: 0.55, // Slightly larger than half a tile for visibility
      color: { r: 50, g: 200, b: 100 }, // Green
      opacity: 0.9,
      animation: 'pulse',
      cubeSize: this.camera.CUBE_SIZE
    });

    // Add to world for proper depth sorting
    this.world.addGroundEffect(this.destinationMarker);
  }

  /**
   * Set up event listeners
   * @private
   */
  _setupEventListeners() {
    const containerEl = document.getElementById('container');
    if (!containerEl) return;

    // Use capture phase to intercept before FacePicker
    containerEl.addEventListener('mousedown', this._handleMouseDown.bind(this), true);
    containerEl.addEventListener('mouseup', this._handleMouseUp.bind(this), true);
    containerEl.addEventListener('touchstart', this._handleTouchStart.bind(this), { passive: false, capture: true });
    containerEl.addEventListener('touchend', this._handleTouchEnd.bind(this), { passive: false, capture: true });
  }

  /**
   * Handle mouse down
   * @private
   */
  _handleMouseDown(event) {
    if (!this.enabled) return;
    if (event.button !== 0) return;

    this.mouseDownPos = { x: event.clientX, y: event.clientY };
  }

  /**
   * Handle mouse up (click)
   * @private
   */
  _handleMouseUp(event) {
    if (!this.enabled) return;
    if (event.button !== 0) return;
    if (!this.mouseDownPos) return;

    // Check if it was a click (minimal movement)
    const dx = event.clientX - this.mouseDownPos.x;
    const dy = event.clientY - this.mouseDownPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    this.mouseDownPos = null;

    if (distance > this.CLICK_THRESHOLD) return;

    // Skip if click was consumed by click-to-attack (enemy was clicked)
    if (window.game && window.game._clickToAttackConsumed) return;

    // Handle the click
    this._handleClick(event);
  }

  /**
   * Handle touch start
   * @private
   */
  _handleTouchStart(event) {
    if (!this.enabled) return;
    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    this.mouseDownPos = { x: touch.clientX, y: touch.clientY };
  }

  /**
   * Handle touch end
   * @private
   */
  _handleTouchEnd(event) {
    if (!this.enabled) return;
    if (!this.mouseDownPos) return;

    // Get end position from changedTouches
    if (event.changedTouches.length === 0) {
      this.mouseDownPos = null;
      return;
    }

    const touch = event.changedTouches[0];
    const dx = touch.clientX - this.mouseDownPos.x;
    const dy = touch.clientY - this.mouseDownPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    this.mouseDownPos = null;

    if (distance > this.CLICK_THRESHOLD) return;

    // Handle the click using touch end position
    this._handleClickAt(touch.clientX, touch.clientY, event.target);
  }

  /**
   * Handle a click event
   * @private
   */
  _handleClick(event) {
    this._handleClickAt(event.clientX, event.clientY, event.target);
  }

  /**
   * Handle a click at specific coordinates
   * @private
   */
  _handleClickAt(clientX, clientY, target) {
    if (!this.player || !this.pathFinder) return;

    // Find the clicked face element
    const faceEl = target.closest ? target.closest('.face') : null;
    if (!faceEl) return;

    // Skip UI elements
    if (target.closest('.sidebar-panel, .top-toolbar, .top-sheet, .settings-panel') || target.closest('.info-panel')) return;

    // Skip ghost previews (editor)
    if (faceEl.closest('.ghost-preview')) return;

    // Find the parent cube element
    const cubeEl = faceEl.closest('.cube');
    if (!cubeEl) return;

    // Find the cuboid instance
    const cuboid = this.world.findCuboidByElement(cubeEl);
    if (!cuboid) return;

    // Use effective cuboid (for fragments)
    const effectiveCuboid = cuboid.isFragment
      ? this.world.cuboids.get(cuboid.parentKey) || cuboid
      : cuboid;

    // If clicking on a noCollision cuboid (water, gas, etc.), find walkable surface below
    // at the exact click position (click "through" the water)
    if (effectiveCuboid.hasNoCollision && effectiveCuboid.hasNoCollision()) {
      Debug.log('ClickToMove', 'Clicked on noCollision cuboid, finding walkable surface below');

      // Convert click position to world coordinates at the cuboid's top surface
      const topY = effectiveCuboid.y + effectiveCuboid.height;
      const CUBE_SIZE = this.camera.CUBE_SIZE;

      const screenX = clientX - window.innerWidth / 2;
      const screenY = clientY - window.innerHeight / 2;
      const renderingYOffset = CUBE_SIZE / 2;
      const adjustedScreenY = screenY - renderingYOffset * this.camera.zoomLevel;

      const faceWorldY = topY * CUBE_SIZE;
      const worldPos = Transform.screenToWorld(screenX, adjustedScreenY, this.camera, faceWorldY);

      const clickX = worldPos.x / CUBE_SIZE;
      const clickZ = -worldPos.z / CUBE_SIZE;

      // Find walkable surface below at the exact click position
      const walkableSurface = this._findWalkableSurfaceBelow(
        clickX,
        topY, // Start from the top of the noCollision cuboid
        clickZ
      );
      if (!walkableSurface) {
        Debug.warn('ClickToMove', 'No walkable surface found below noCollision cuboid');
        return;
      }
      this._navigateToDestination(walkableSurface);
      return;
    }

    // Determine which face was clicked
    const faceType = this._getFaceType(faceEl);
    if (!faceType) return;

    // Calculate world position based on face type and click position
    const destination = this._calculateDestination(cuboid, faceType, clientX, clientY);
    if (!destination) return;

    this._navigateToDestination(destination);
  }

  /**
   * Navigate the player to a destination
   * @private
   * @param {Object} destination - { x, y, z } world position
   */
  _navigateToDestination(destination) {
    if (!this.player || !this.pathFinder) return;

    Debug.log('ClickToMove', 'Destination calculated:', destination);
    Debug.log('ClickToMove', '🔴 DESTINATION:', destination.x.toFixed(2), destination.y.toFixed(2), destination.z.toFixed(2));

    // Find path from player to destination
    const startPos = {
      x: this.player.x,
      y: this.player.y,
      z: this.player.z
    };

    Debug.log('ClickToMove', 'Player position:', startPos);
    Debug.log('ClickToMove', '🔴 START:', startPos.x.toFixed(2), startPos.y.toFixed(2), startPos.z.toFixed(2));

    const path = this.pathFinder.findPath(startPos, destination);

    Debug.log('ClickToMove', '🔴 PATH HAS', path ? path.length : 0, 'WAYPOINTS');
    if (path && path.length > 0) {
      Debug.log('ClickToMove', '🔴 FIRST WAYPOINT:', path[0].x.toFixed(2), path[0].y.toFixed(2), path[0].z.toFixed(2));
    }

    if (path && path.length > 0) {
      Debug.log('ClickToMove', 'DEST y=' + destination.y.toFixed(1) + ' | Path found with', path.length, 'waypoints');
      // Log what we're about to pass to setPath
      for (let i = 0; i < Math.min(path.length, 5); i++) {
        const wp = path[i];
        Debug.log('ClickToMove', '  PASSING [' + i + '] x=' + wp.x.toFixed(2) + ' y=' + wp.y.toFixed(2) + ' z=' + wp.z.toFixed(2));
      }
      // Set path on player's path follower
      if (this.player.pathFollower) {
        this.player.pathFollower.setPath(path);
        this._showMarker(destination);
      }
    } else {
      Debug.warn('ClickToMove', 'No path found to destination');
    }
  }

  /**
   * Find a walkable surface below a given position (for noCollision cuboids)
   * @private
   * @param {number} x - X position
   * @param {number} y - Y position to search below
   * @param {number} z - Z position
   * @returns {Object|null} { x, y, z } of walkable surface or null
   */
  _findWalkableSurfaceBelow(x, y, z) {
    if (!this.navGraph) return null;

    let bestNode = null;
    let bestY = -Infinity;

    // Search all nodes that contain this XZ position and are at or below the given Y
    for (const node of this.navGraph.getAllNodes()) {
      if (!node.containsPoint(x, z)) continue;

      // Only consider surfaces at or below the given Y (floor can be at same level as water bottom)
      if (node.y > y) continue;

      // Find the highest walkable surface below
      if (node.y > bestY) {
        const clampedX = Math.max(node.minX + 0.5, Math.min(node.maxX - 0.5, x));
        const clampedZ = Math.max(node.minZ + 0.5, Math.min(node.maxZ - 0.5, z));

        // Verify this surface has head clearance
        if (this._hasHeadClearance(clampedX, node.y, clampedZ)) {
          bestY = node.y;
          bestNode = node;
        }
      }
    }

    if (bestNode) {
      const clampedX = Math.max(bestNode.minX + 0.5, Math.min(bestNode.maxX - 0.5, x));
      const clampedZ = Math.max(bestNode.minZ + 0.5, Math.min(bestNode.maxZ - 0.5, z));
      return { x: clampedX, y: bestNode.y, z: clampedZ };
    }

    return null;
  }

  /**
   * Get face type from element
   * @private
   */
  _getFaceType(faceEl) {
    const faceTypes = ['top', 'bottom', 'front', 'back', 'left', 'right'];
    for (const type of faceTypes) {
      if (faceEl.classList.contains(type)) {
        return type;
      }
    }
    return null;
  }

  /**
   * Calculate destination world position based on clicked face
   * @private
   */
  _calculateDestination(cuboid, faceType, clientX, clientY) {
    // Use effective cuboid (for fragments)
    const effectiveCuboid = cuboid.isFragment
      ? this.world.cuboids.get(cuboid.parentKey) || cuboid
      : cuboid;

    if (faceType === 'top') {
      // Click on top face: use screen-to-world to find exact click position
      // Following the same pattern as VoxelEditor's AddTool
      const topY = effectiveCuboid.y + effectiveCuboid.height;
      const CUBE_SIZE = this.camera.CUBE_SIZE;

      // Convert client coordinates to screen coordinates relative to camera center
      const screenX = clientX - window.innerWidth / 2;
      const screenY = clientY - window.innerHeight / 2;

      // Account for rendering Y offset (Cuboid rendering adds CUBE_SIZE/2 to screen Y)
      const renderingYOffset = CUBE_SIZE / 2;
      const adjustedScreenY = screenY - renderingYOffset * this.camera.zoomLevel;

      // Convert to world pixel space at the face's Y level
      const faceWorldY = topY * CUBE_SIZE;
      const worldPos = Transform.screenToWorld(screenX, adjustedScreenY, this.camera, faceWorldY);

      // Convert from pixel space to world units (and negate Z as per coordinate system)
      const worldX = worldPos.x / CUBE_SIZE;
      const worldZ = -worldPos.z / CUBE_SIZE;

      // Clamp to cuboid bounds (with small margin)
      const margin = 0.3;
      const clampedX = Math.max(
        effectiveCuboid.x + margin,
        Math.min(effectiveCuboid.x + effectiveCuboid.width - margin, worldX)
      );
      const clampedZ = Math.max(
        effectiveCuboid.z + margin,
        Math.min(effectiveCuboid.z + effectiveCuboid.depth - margin, worldZ)
      );

      // Check if this top face position is accessible (has head clearance)
      // For stacked structures, the top face might be blocked by another level
      if (this._hasHeadClearance(clampedX, topY, clampedZ)) {
        return {
          x: clampedX,
          y: topY,
          z: clampedZ
        };
      }

      // Top face is blocked - find the topmost accessible surface at this position
      Debug.log('ClickToMove', 'Top face blocked, finding accessible surface');
      const accessibleSurface = this._findTopmostAccessibleSurface(clampedX, clampedZ, topY);
      if (accessibleSurface) {
        return accessibleSurface;
      }

      // Fallback to the clicked position even if blocked
      return {
        x: clampedX,
        y: topY,
        z: clampedZ
      };
    } else if (faceType === 'bottom') {
      // Click on bottom face: find walkable surface below
      return this._findNearestWalkable(
        effectiveCuboid.x + effectiveCuboid.width / 2,
        effectiveCuboid.y - 0.5,
        effectiveCuboid.z + effectiveCuboid.depth / 2
      );
    } else {
      // Click on side face: handle stacked cuboids and blocked faces
      return this._calculateSideFaceDestination(effectiveCuboid, faceType);
    }
  }

  /**
   * Calculate destination for side face clicks, handling stacked cuboids
   * @private
   */
  _calculateSideFaceDestination(cuboid, faceType) {
    const offset = ClickToMoveHandler.FACE_OFFSETS[faceType];
    if (!offset) return null;

    const cuboidCenterX = cuboid.x + cuboid.width / 2;
    const cuboidCenterZ = cuboid.z + cuboid.depth / 2;
    const cuboidTopY = cuboid.y + cuboid.height;
    const STEP_HEIGHT = 1.0;

    // Target position in front of the clicked face
    const targetX = cuboidCenterX + offset.x * (cuboid.width / 2 + 0.5);
    const targetZ = cuboidCenterZ + offset.z * (cuboid.depth / 2 + 0.5);

    // Get player's current surface height
    const playerY = this.player ? this.player.y : 0;

    // Check if this face forms a wall relative to the player's position
    // A wall is formed if:
    // 1. The clicked cuboid's bottom is higher than player can step up to, OR
    // 2. There are stacked cuboids making the total height too high
    const clickedFaceBottom = cuboid.y;
    const heightAbovePlayer = clickedFaceBottom - playerY;

    // If the face is more than STEP_HEIGHT above player, it's a wall from their perspective
    const isWallFromPlayerPosition = heightAbovePlayer > STEP_HEIGHT;

    // Also check if there are stacked cuboids on top making it a wall
    const faceBlockedByStack = this._isFaceBlockedByStackedCuboids(cuboid, faceType, targetX, targetZ);

    if (isWallFromPlayerPosition || faceBlockedByStack) {
      Debug.log('ClickToMove', 'Face is a wall (heightAbovePlayer:', heightAbovePlayer.toFixed(1),
                  ', blockedByStack:', faceBlockedByStack, '), finding accessible route');

      // Find the complete stack at this location and route to its top via accessible side
      const stackTop = this._findStackTopAndAccessibleRoute(cuboid, offset, playerY);
      if (stackTop) {
        return stackTop;
      }

      // Fallback: find any accessible surface at the target XZ
      const accessibleSurface = this._findTopmostAccessibleSurface(targetX, targetZ, playerY);
      if (accessibleSurface) {
        return accessibleSurface;
      }

      // Ultimate fallback: find the top of the stack
      const fallbackTop = this._findStackTopSurface(cuboid, offset);
      if (fallbackTop) {
        return fallbackTop;
      }
    }

    // Not a wall - find nearest walkable at the target position
    // Use the face height level as the starting Y for search
    return this._findNearestWalkable(targetX, cuboidTopY, targetZ);
  }

  /**
   * Find the top of a stacked structure and determine an accessible approach route
   * This handles clicking on walls by finding where the player CAN climb from
   * @private
   */
  _findStackTopAndAccessibleRoute(clickedCuboid, clickedOffset, playerY) {
    if (!this.collisionSystem) return null;

    const STEP_HEIGHT = 1.0;

    // Find all cuboids in this stack (from floor up)
    const stackCuboids = this._findFullStack(clickedCuboid);
    if (stackCuboids.length === 0) {
      stackCuboids.push(clickedCuboid);
    }

    // Find the topmost cuboid in the stack
    let highestCuboid = stackCuboids[0];
    for (const c of stackCuboids) {
      if (c.y + c.height > highestCuboid.y + highestCuboid.height) {
        highestCuboid = c;
      }
    }

    const stackTopY = highestCuboid.y + highestCuboid.height;
    const stackCenterX = highestCuboid.x + highestCuboid.width / 2;
    const stackCenterZ = highestCuboid.z + highestCuboid.depth / 2;

    // Get player's current position to prefer the closest accessible side
    const playerX = this.player ? this.player.x : stackCenterX;
    const playerZ = this.player ? this.player.z : stackCenterZ;

    // Check each direction to find an accessible approach
    const directions = [
      { name: 'front', offset: { x: 0, z: 1 } },
      { name: 'back', offset: { x: 0, z: -1 } },
      { name: 'left', offset: { x: -1, z: 0 } },
      { name: 'right', offset: { x: 1, z: 0 } }
    ];

    // Score each direction by accessibility and DISTANCE FROM PLAYER (prefer closest)
    const scoredDirections = [];

    for (const dir of directions) {
      // Check if this side has a climbable path (step-by-step access)
      const accessible = this._isStackSideAccessible(stackCuboids, dir.offset, playerY, STEP_HEIGHT);

      if (accessible) {
        // Calculate the approach position for this direction
        const approachX = stackCenterX + dir.offset.x * (highestCuboid.width / 2 + 1);
        const approachZ = stackCenterZ + dir.offset.z * (highestCuboid.depth / 2 + 1);

        // Score by distance from player - PREFER CLOSEST ACCESSIBLE SIDE
        const distFromPlayer = Math.sqrt(
          Math.pow(approachX - playerX, 2) + Math.pow(approachZ - playerZ, 2)
        );

        Debug.log('ClickToMove', 'Side', dir.name, 'accessible, dist from player:', distFromPlayer.toFixed(1));
        scoredDirections.push({ dir, score: distFromPlayer });
      }
    }

    if (scoredDirections.length === 0) {
      Debug.log('ClickToMove', 'No accessible side found for stack');
      return null;
    }

    // Pick the CLOSEST accessible direction to player
    scoredDirections.sort((a, b) => a.score - b.score);
    const bestDir = scoredDirections[0].dir;

    // Return a destination on top of the stack, positioned toward the accessible side
    const topCenterX = highestCuboid.x + highestCuboid.width / 2;
    const topCenterZ = highestCuboid.z + highestCuboid.depth / 2;

    // Position slightly toward the accessible edge
    const destX = topCenterX + bestDir.offset.x * (highestCuboid.width / 2 - 0.5);
    const destZ = topCenterZ + bestDir.offset.z * (highestCuboid.depth / 2 - 0.5);

    Debug.log('ClickToMove', 'Routing to stack top via', bestDir.name, 'side (closest to player)');
    return { x: destX, y: stackTopY, z: destZ };
  }

  /**
   * Find all cuboids that form a vertical stack with the given cuboid
   * @private
   */
  _findFullStack(cuboid) {
    if (!this.collisionSystem) return [cuboid];

    const stack = [];
    const checked = new Set();

    // Query a tall column at this XZ position
    const queryBox = {
      minX: cuboid.x - 0.1,
      maxX: cuboid.x + cuboid.width + 0.1,
      minY: -100, // Search from below
      maxY: cuboid.y + cuboid.height + 100, // And above
      minZ: cuboid.z - 0.1,
      maxZ: cuboid.z + cuboid.depth + 0.1
    };

    const candidates = this.collisionSystem.queryCuboids(queryBox);

    // Filter to cuboids that overlap with the clicked cuboid's XZ footprint
    for (const c of candidates) {
      // Skip non-collider cuboids (water, gas, etc.)
      if (c.hasNoCollision && c.hasNoCollision()) continue;

      const key = `${c.x},${c.y},${c.z}`;
      if (checked.has(key)) continue;
      checked.add(key);

      // Check XZ overlap
      const overlapX = Math.max(0,
        Math.min(c.x + c.width, cuboid.x + cuboid.width) - Math.max(c.x, cuboid.x)
      );
      const overlapZ = Math.max(0,
        Math.min(c.z + c.depth, cuboid.z + cuboid.depth) - Math.max(c.z, cuboid.z)
      );

      if (overlapX > 0.1 && overlapZ > 0.1) {
        stack.push(c);
      }
    }

    // Sort by Y position (bottom to top)
    stack.sort((a, b) => a.y - b.y);

    return stack;
  }

  /**
   * Check if a side of the stack is accessible (can be climbed step by step)
   * @private
   */
  _isStackSideAccessible(stackCuboids, dirOffset, startY, stepHeight) {
    if (stackCuboids.length === 0) return false;

    // Sort cuboids by Y
    const sorted = [...stackCuboids].sort((a, b) => a.y - b.y);

    // Check if we can climb from startY to each level in sequence
    let currentY = startY;

    for (const cuboid of sorted) {
      const cuboidSurfaceY = cuboid.y + cuboid.height;

      // Skip cuboids below our current level
      if (cuboidSurfaceY <= currentY + 0.1) continue;

      // Check if this step is climbable
      const stepUp = cuboidSurfaceY - currentY;
      if (stepUp > stepHeight + 0.1) {
        return false;
      }

      // Check if this surface has exposed area to stand on.
      // A surface fully covered by the next cuboid above (smooth wall) is not usable.
      const isCovered = sorted.some(other => {
        if (other === cuboid) return false;
        // Must be sitting directly on this cuboid's surface
        if (Math.abs(other.y - cuboidSurfaceY) > 0.1) return false;
        // Check exposed margins (same logic as NavMeshBuilder._isWalkableSurface)
        const exposedLeft = Math.max(0, other.x - cuboid.x);
        const exposedRight = Math.max(0, (cuboid.x + cuboid.width) - (other.x + other.width));
        const exposedFront = Math.max(0, other.z - cuboid.z);
        const exposedBack = Math.max(0, (cuboid.z + cuboid.depth) - (other.z + other.depth));
        const maxExposed = Math.max(exposedLeft, exposedRight, exposedFront, exposedBack);
        return maxExposed < ClickToMoveHandler.CHARACTER_WIDTH;
      });

      if (isCovered) {
        return false; // Can't stand here - surface is fully covered (smooth wall)
      }

      // Check if approach from this direction is blocked by a wall
      const approachX = cuboid.x + cuboid.width / 2 + dirOffset.x * (cuboid.width / 2 + 0.5);
      const approachZ = cuboid.z + cuboid.depth / 2 + dirOffset.z * (cuboid.depth / 2 + 0.5);

      if (this._hasWallBlockingApproach(approachX, approachZ, currentY, cuboidSurfaceY, dirOffset)) {
        return false;
      }

      currentY = cuboidSurfaceY;
    }

    return true;
  }

  /**
   * Check if there's a wall blocking approach from a direction
   * @private
   */
  _hasWallBlockingApproach(approachX, approachZ, fromY, toY, dirOffset) {
    if (!this.collisionSystem) return false;

    const CHARACTER_WIDTH = ClickToMoveHandler.CHARACTER_WIDTH;
    const CHARACTER_HEIGHT = ClickToMoveHandler.CHARACTER_HEIGHT;
    const halfWidth = CHARACTER_WIDTH / 2;

    // Query the space between approach position and the target surface
    const queryBox = {
      minX: approachX - halfWidth,
      maxX: approachX + halfWidth,
      minY: fromY + 0.1,
      maxY: Math.max(fromY, toY) + CHARACTER_HEIGHT,
      minZ: approachZ - halfWidth,
      maxZ: approachZ + halfWidth
    };

    const obstacles = this.collisionSystem.queryCuboids(queryBox);

    for (const obstacle of obstacles) {
      // Skip non-collider cuboids (water, gas, etc.)
      if (obstacle.hasNoCollision && obstacle.hasNoCollision()) continue;

      const obsBottom = obstacle.y;
      const obsTop = obstacle.y + obstacle.height;

      // Skip floors at our current level
      if (Math.abs(obsTop - fromY) < 0.1) continue;
      if (Math.abs(obsTop - toY) < 0.1) continue;

      // Check if this obstacle blocks vertical passage
      if (obsBottom < toY + CHARACTER_HEIGHT && obsTop > fromY + 0.5) {
        // Check XZ overlap
        const obsMinX = obstacle.x;
        const obsMaxX = obstacle.x + obstacle.width;
        const obsMinZ = obstacle.z;
        const obsMaxZ = obstacle.z + obstacle.depth;

        if (queryBox.minX < obsMaxX && queryBox.maxX > obsMinX &&
            queryBox.minZ < obsMaxZ && queryBox.maxZ > obsMinZ) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Check if a side face is blocked by stacked cuboids above it
   * This detects when clicking on a "wall" face of a multi-level structure
   * A face is blocked if there's a cuboid stacked on top that:
   * 1. Sits on the clicked cuboid's top surface
   * 2. Extends too high to step over (forms a wall)
   * 3. Covers the clicked face's edge (blocks approach from that direction)
   * @private
   */
  _isFaceBlockedByStackedCuboids(cuboid, faceType, targetX, targetZ) {
    if (!this.collisionSystem) return false;

    const cuboidTopY = cuboid.y + cuboid.height;
    const STEP_HEIGHT = 1.0; // Maximum height that can be stepped over

    // Get the face edge coordinate
    let faceEdgeX, faceEdgeZ;
    switch (faceType) {
      case 'left': faceEdgeX = cuboid.x; break;
      case 'right': faceEdgeX = cuboid.x + cuboid.width; break;
      case 'back': faceEdgeZ = cuboid.z; break;
      case 'front': faceEdgeZ = cuboid.z + cuboid.depth; break;
    }

    // Query for obstacles on top of the clicked cuboid
    const queryBox = {
      minX: cuboid.x - 0.1,
      maxX: cuboid.x + cuboid.width + 0.1,
      minY: cuboidTopY - 0.1,
      maxY: cuboidTopY + 10, // Search high for stacked structures
      minZ: cuboid.z - 0.1,
      maxZ: cuboid.z + cuboid.depth + 0.1
    };

    const obstacles = this.collisionSystem.queryCuboids(queryBox);

    for (const obstacle of obstacles) {
      if (obstacle === cuboid) continue;

      // Skip non-collider cuboids (water, gas, etc.)
      if (obstacle.hasNoCollision && obstacle.hasNoCollision()) continue;

      const obsBottom = obstacle.y;
      const obsTop = obstacle.y + obstacle.height;

      // Is it stacked on the clicked cuboid? (sitting on or near the top surface)
      if (obsBottom > cuboidTopY + 0.5) continue;

      // Does it form a wall? (too tall to step over from the clicked cuboid's surface)
      if (obsTop <= cuboidTopY + STEP_HEIGHT) continue;

      // Does it cover the clicked face's edge?
      const obsMinX = obstacle.x;
      const obsMaxX = obstacle.x + obstacle.width;
      const obsMinZ = obstacle.z;
      const obsMaxZ = obstacle.z + obstacle.depth;

      if (faceType === 'left' || faceType === 'right') {
        // Check if obstacle reaches the X edge AND overlaps in Z with the clicked cuboid
        const reachesEdge = (faceEdgeX >= obsMinX - 0.1 && faceEdgeX <= obsMaxX + 0.1);
        const zOverlap = (cuboid.z < obsMaxZ && cuboid.z + cuboid.depth > obsMinZ);
        if (reachesEdge && zOverlap) {
          Debug.log('ClickToMove', 'Face blocked by stacked wall at edge', faceType);
          return true;
        }
      } else {
        // Check if obstacle reaches the Z edge AND overlaps in X with the clicked cuboid
        const reachesEdge = (faceEdgeZ >= obsMinZ - 0.1 && faceEdgeZ <= obsMaxZ + 0.1);
        const xOverlap = (cuboid.x < obsMaxX && cuboid.x + cuboid.width > obsMinX);
        if (reachesEdge && xOverlap) {
          Debug.log('ClickToMove', 'Face blocked by stacked wall at edge', faceType);
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Find the topmost accessible surface at a given XZ position
   * Works by finding all surfaces at this position and selecting the highest
   * one that has head clearance
   * @private
   */
  _findTopmostAccessibleSurface(x, z, minY) {
    if (!this.navGraph) return null;

    const CHARACTER_HEIGHT = ClickToMoveHandler.CHARACTER_HEIGHT;
    let bestNode = null;
    let bestY = -Infinity;

    // Search all nodes that contain this XZ position
    for (const node of this.navGraph.getAllNodes()) {
      if (!node.containsPoint(x, z)) continue;

      // Only consider surfaces at or above the minimum Y
      if (node.y < minY - 1) continue;

      // Check if this surface is higher than current best
      if (node.y > bestY) {
        // Verify this surface has head clearance (is actually walkable)
        if (this._hasHeadClearance(x, node.y, z)) {
          bestY = node.y;
          bestNode = node;
        }
      }
    }

    if (bestNode) {
      // Return position clamped to surface bounds
      const clampedX = Math.max(bestNode.minX + 0.5, Math.min(bestNode.maxX - 0.5, x));
      const clampedZ = Math.max(bestNode.minZ + 0.5, Math.min(bestNode.maxZ - 0.5, z));
      return { x: clampedX, y: bestNode.y, z: clampedZ };
    }

    return null;
  }

  /**
   * Find the top surface of a stacked structure
   * @private
   */
  _findStackTopSurface(baseCuboid, offset) {
    if (!this.collisionSystem) return null;

    const cuboidCenterX = baseCuboid.x + baseCuboid.width / 2;
    const cuboidCenterZ = baseCuboid.z + baseCuboid.depth / 2;

    // Search upward from the base cuboid to find all stacked cuboids
    let currentY = baseCuboid.y + baseCuboid.height;
    let highestY = currentY;
    let highestCuboid = baseCuboid;

    // Query for cuboids stacked above
    const queryBox = {
      minX: baseCuboid.x - 0.5,
      maxX: baseCuboid.x + baseCuboid.width + 0.5,
      minY: currentY,
      maxY: currentY + 20, // Search up to 20 units above
      minZ: baseCuboid.z - 0.5,
      maxZ: baseCuboid.z + baseCuboid.depth + 0.5
    };

    const stackedCuboids = this.collisionSystem.queryCuboids(queryBox);

    for (const stacked of stackedCuboids) {
      if (stacked === baseCuboid) continue;

      // Skip non-collider cuboids (water, gas, etc.)
      if (stacked.hasNoCollision && stacked.hasNoCollision()) continue;

      const stackedTop = stacked.y + stacked.height;
      if (stackedTop > highestY) {
        // Check if this cuboid overlaps with the base cuboid's XZ footprint
        const overlapX = Math.max(0,
          Math.min(stacked.x + stacked.width, baseCuboid.x + baseCuboid.width) -
          Math.max(stacked.x, baseCuboid.x)
        );
        const overlapZ = Math.max(0,
          Math.min(stacked.z + stacked.depth, baseCuboid.z + baseCuboid.depth) -
          Math.max(stacked.z, baseCuboid.z)
        );

        if (overlapX > 0 && overlapZ > 0) {
          highestY = stackedTop;
          highestCuboid = stacked;
        }
      }
    }

    // Now find a walkable position on the top surface
    // In the direction the player was trying to go (based on face offset)
    const topCenterX = highestCuboid.x + highestCuboid.width / 2;
    const topCenterZ = highestCuboid.z + highestCuboid.depth / 2;

    // Target is on the top surface, but toward the edge the player clicked on
    const targetX = topCenterX + offset.x * (highestCuboid.width / 2 - 0.5);
    const targetZ = topCenterZ + offset.z * (highestCuboid.depth / 2 - 0.5);

    // Make sure target is within bounds
    const clampedX = Math.max(highestCuboid.x + 0.3, Math.min(highestCuboid.x + highestCuboid.width - 0.3, targetX));
    const clampedZ = Math.max(highestCuboid.z + 0.3, Math.min(highestCuboid.z + highestCuboid.depth - 0.3, targetZ));

    return { x: clampedX, y: highestY, z: clampedZ };
  }

  /**
   * Check if there's head clearance at a position
   * @private
   */
  _hasHeadClearance(x, surfaceY, z) {
    if (!this.collisionSystem) return true;

    const CHARACTER_HEIGHT = ClickToMoveHandler.CHARACTER_HEIGHT;
    const halfWidth = ClickToMoveHandler.CHARACTER_WIDTH / 2;

    const queryBox = {
      minX: x - halfWidth,
      maxX: x + halfWidth,
      minY: surfaceY + 0.1,
      maxY: surfaceY + CHARACTER_HEIGHT,
      minZ: z - halfWidth,
      maxZ: z + halfWidth
    };

    const obstacles = this.collisionSystem.queryCuboids(queryBox);

    for (const obstacle of obstacles) {
      // Skip non-collider cuboids (water, gas, etc.)
      if (obstacle.hasNoCollision && obstacle.hasNoCollision()) continue;

      const obsTop = obstacle.y + obstacle.height;
      const obsBottom = obstacle.y;

      // Skip if this is the floor we're standing on
      if (Math.abs(obsTop - surfaceY) < 0.1) continue;
      if (obsTop <= surfaceY + 0.1) continue;

      // Check if obstacle blocks head space
      if (obsBottom < surfaceY + CHARACTER_HEIGHT) {
        const obsMinX = obstacle.x;
        const obsMaxX = obstacle.x + obstacle.width;
        const obsMinZ = obstacle.z;
        const obsMaxZ = obstacle.z + obstacle.depth;

        if (x >= obsMinX - halfWidth && x <= obsMaxX + halfWidth &&
            z >= obsMinZ - halfWidth && z <= obsMaxZ + halfWidth) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Find nearest walkable surface to a position
   * Improved to handle stacked structures by preferring accessible surfaces
   * @private
   */
  _findNearestWalkable(x, y, z) {
    if (!this.navGraph) {
      return { x, y: 0, z };
    }

    // First, try to find nodes that actually contain this XZ point
    // and select the best one based on accessibility
    const candidateNodes = [];

    for (const node of this.navGraph.getAllNodes()) {
      // Check if node contains the XZ point (or is very close)
      const withinX = x >= node.minX - 0.5 && x <= node.maxX + 0.5;
      const withinZ = z >= node.minZ - 0.5 && z <= node.maxZ + 0.5;

      if (withinX && withinZ) {
        const yDiff = Math.abs(node.y - y);
        if (yDiff < 10) { // Within reasonable height range
          candidateNodes.push({ node, yDiff });
        }
      }
    }

    if (candidateNodes.length > 0) {
      // Sort by Y difference (prefer surfaces closer to target Y)
      candidateNodes.sort((a, b) => a.yDiff - b.yDiff);

      // For stacked structures, prefer the surface that's closest to target Y
      // AND has head clearance (is actually accessible)
      for (const { node } of candidateNodes) {
        const clampedX = Math.max(node.minX + 0.5, Math.min(node.maxX - 0.5, x));
        const clampedZ = Math.max(node.minZ + 0.5, Math.min(node.maxZ - 0.5, z));

        // Check if this position has head clearance
        if (this._hasHeadClearance(clampedX, node.y, clampedZ)) {
          return { x: clampedX, y: node.y, z: clampedZ };
        }
      }

      // If no candidate has head clearance, use the closest one anyway
      const bestNode = candidateNodes[0].node;
      const clampedX = Math.max(bestNode.minX + 0.5, Math.min(bestNode.maxX - 0.5, x));
      const clampedZ = Math.max(bestNode.minZ + 0.5, Math.min(bestNode.maxZ - 0.5, z));
      return { x: clampedX, y: bestNode.y, z: clampedZ };
    }

    // Fallback to standard nearest node search
    const node = this.navGraph.findNearestNode(x, y, z, 10);
    if (node) {
      // Return position on node closest to click point
      const clampedX = Math.max(node.minX + 0.5, Math.min(node.maxX - 0.5, x));
      const clampedZ = Math.max(node.minZ + 0.5, Math.min(node.maxZ - 0.5, z));
      return { x: clampedX, y: node.y, z: clampedZ };
    }

    return { x, y: 0, z };
  }

  /**
   * Show the destination marker at the given world position
   * @private
   */
  _showMarker(destination) {
    if (!this.destinationMarker) return;

    // Update position and show the marker
    this.destinationMarker.setPosition(destination.x, destination.y, destination.z);
    this.destinationMarker.show();
    this.markerDestination = destination;
  }

  /**
   * Hide the destination marker with fade out animation
   * @private
   */
  _hideMarker() {
    if (this.destinationMarker) {
      // Use fadeOut for smooth transition instead of instant hide
      this.destinationMarker.fadeOut();
    }
    this.markerDestination = null;
  }

  /**
   * Update marker position (call in game loop if camera moves)
   * Note: With GroundEffect, the World handles transform updates during sorting,
   * but this method can be called to update the marker destination if needed.
   */
  updateMarker() {
    // Transform updates are now handled by World.sortAndRenderCuboids
    // This method is kept for API compatibility
  }
}
